/**
 * UpgradeCtaBanner — emerald gradient banner on the dashboard for hot pilots.
 *
 * Only renders when /api/upgrade-cta/state returns visible:true. Dismissible
 * (14-day cool-down via backend).
 */
import React, { useEffect, useState } from 'react';
import { Rocket, X, ArrowUpRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UpgradeCtaBanner() {
  const [state, setState] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const r = await fetch(`${API}/api/upgrade-cta/state`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setState(await r.json());
      } catch { /* ignore */ }
    })();
  }, []);

  const dismiss = async () => {
    setDismissing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/upgrade-cta/dismiss`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setState({ ...state, visible: false });
    } finally { setDismissing(false); }
  };

  if (!state || !state.visible) return null;

  return (
    <div
      className="relative rounded-xl border border-emerald-400/40 bg-emerald-600 bg-gradient-to-r from-emerald-600 via-emerald-500 to-cyan-600 p-5 mb-6 shadow-lg shadow-emerald-500/30"
      data-testid="upgrade-cta-banner"
    >
      <button
        onClick={dismiss}
        disabled={dismissing}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-emerald-50 hover:text-white"
        data-testid="upgrade-cta-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-4">
        <div className="inline-flex w-12 h-12 rounded-lg bg-white/20 backdrop-blur items-center justify-center shrink-0">
          <Rocket className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 text-white rounded px-2 py-0.5">
              ACTIVATION {state.score}/100
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-50">Hot pilot · top 30%</span>
          </div>
          <h3 className="text-xl font-extrabold text-white mb-1">{state.headline}</h3>
          {state.subline && <p className="text-sm text-emerald-50">{state.subline}</p>}
        </div>
        <a
          href={state.cta_url}
          className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold text-sm px-4 py-2.5 rounded-md transition-colors inline-flex items-center gap-1 shrink-0 shadow-md"
          data-testid="upgrade-cta-link"
        >
          {state.cta_label} <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
